import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Page from "@/components/Page";
import ButtonLink from "@/components/ButtonLink";
import { GUIDELINES } from "@/common/constants";
import { getPartnerConfig } from "@/common/partnerConfig";
import { Check } from "lucide-react";
import { trackEvent } from "@/lib/amplitude";
import { PiHandTap } from "react-icons/pi";

export default function OnboardingVariantB() {
  const { t } = useTranslation();

  const [clickedGuidelines, setClickedGuidelines] = useState<Set<number>>(new Set());

  const placeRef = window.localStorage.getItem("placeRef");
  const partnerConfig = getPartnerConfig(placeRef);
  const translationSuffix = partnerConfig?.guidelines.customTranslationSuffix ?? "";

  // Compute guidelines array synchronously based on placeRef
  const guidelines = partnerConfig?.guidelines.showSubjectGuideline
    ? GUIDELINES
    : GUIDELINES.slice(0, 3);

  const allClicked = clickedGuidelines.size === guidelines.length;

  // Track page view on mount
  useEffect(() => {
    trackEvent("onboarding_started", {
      variant: "B",
      experiment: "onboarding_flow_test",
      placeRef,
      partner: partnerConfig?.id,
      guidelinesCount: guidelines.length,
      source: placeRef ? "qr_code" : "direct",
    });

    const startTime = Date.now();

    return () => {
      if (!allClicked) {
        trackEvent("onboarding_abandoned", {
          variant: "B",
          experiment: "onboarding_flow_test",
          placeRef,
          partner: partnerConfig?.id,
          timeSpent: Date.now() - startTime,
          clickedCount: clickedGuidelines.size,
        });
      }
    };
  }, []);

  const handleGuidelineClick = (index: number) => {
    if (!clickedGuidelines.has(index)) {
      setClickedGuidelines((prev) => new Set([...prev, index]));
      trackEvent("guideline_clicked", {
        variant: "B",
        experiment: "onboarding_flow_test",
        guidelineIndex: index,
        guidelineName: guidelines[index].titleKey,
        placeRef,
        partner: partnerConfig?.id,
        clickedCount: clickedGuidelines.size + 1,
      });
    }
  };

  const handleOnboardingComplete = () => {
    trackEvent("onboarding_completed", {
      variant: "B",
      experiment: "onboarding_flow_test",
      placeRef,
      partner: partnerConfig?.id,
      guidelinesCount: guidelines.length,
      allClicked: true,
    });
  };

  return (
    <Page
      lngChange
      title={t("guidelines.title")}
      action={
        allClicked ? (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              damping: 15,
              stiffness: 400,
            }}
          >
            <ButtonLink to="/upload" onClick={handleOnboardingComplete}>
              {t("guidelines.buttonText")}
            </ButtonLink>
          </motion.div>
        ) : (
          <motion.div className="flex h-[60px] w-full items-center justify-center rounded-full bg-medium-gray text-lg font-semibold text-gray-100">
            {clickedGuidelines.size}/{guidelines.length}
          </motion.div>
        )
      }
    >
      <div className="mx-auto my-4 max-w-3xl space-y-1 px-4 text-center">
        <p className="text-xl font-bold">{t("guidelines.welcome.title")}</p>
        <p className="text-sm text-foreground/80">
          {t("guidelines.welcome.description")}
        </p>
      </div>

      <ul className="mx-auto flex w-full max-w-96 flex-col items-center justify-center gap-3 rounded-3xl border border-medium-gray bg-dark-gray px-3 py-4">
        {guidelines.map(({ Icon, titleKey, labelKey }, i) => {
          const isClicked = clickedGuidelines.has(i);

          return (
            <motion.li
              key={i}
              onClick={() => handleGuidelineClick(i)}
              className="relative w-full cursor-pointer"
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: i * 0.1,
                type: "spring",
                damping: 20,
                stiffness: 300,
              }}
            >
              <motion.div
                className="grid w-full grid-cols-[auto_1fr] items-center gap-3 rounded-2xl p-3 transition-colors"
                animate={{
                  backgroundColor: isClicked ? "rgba(92, 255, 92, 0.1)" : "transparent",
                  scale: isClicked ? [1, 1.05, 1] : 1,
                }}
                transition={{
                  backgroundColor: { duration: 0.3 },
                  scale: { duration: 0.4, ease: "easeOut" },
                }}
              >
                <div className="relative">
                  <motion.div
                    animate={{
                      scale: isClicked ? 1 : [1, 1.1, 1],
                      boxShadow: isClicked
                        ? "inset 0 0px 20px rgba(92, 255, 92, 0.5)"
                        : "inset 0 0px 10px rgba(255, 255, 255, 0.3)",
                      backgroundColor: isClicked
                        ? "rgba(92, 255, 92, 0.1)"
                        : "transparent",
                    }}
                    transition={{
                      scale: {
                        duration: 2,
                        repeat: isClicked ? 0 : Infinity,
                        ease: "easeInOut",
                      },
                      boxShadow: { duration: 0.4 },
                    }}
                    className="rounded-2xl bg-medium-gray/50 p-3"
                  >
                    <Icon
                      size={40}
                      className={`fill-foreground transition-all duration-300 ${
                        isClicked ? "text-primary" : "text-foreground opacity-60"
                      }`}
                    />
                  </motion.div>

                  {/* Checkmark overlay */}
                  <AnimatePresence>
                    {isClicked && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0, rotate: 180 }}
                        transition={{
                          type: "spring",
                          damping: 15,
                          stiffness: 400,
                        }}
                        className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 shadow-lg"
                      >
                        <Check size={14} className="text-white" strokeWidth={3} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <motion.div
                  className="w-full text-start"
                  animate={{
                    opacity: isClicked ? 1 : 0.6,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="mb-0.5 text-base font-semibold text-foreground">
                    {t(titleKey)}
                  </h3>
                  <p className="text-xs text-foreground/80">
                    {t(`${labelKey}${translationSuffix}`, {
                      defaultValue: t(labelKey),
                    })}
                  </p>
                </motion.div>
              </motion.div>
            </motion.li>
          );
        })}
      </ul>

      {/* Tap instruction caption */}
      {!allClicked && (
        <motion.div className="mx-auto mt-3 flex items-center justify-center gap-2">
          <PiHandTap size={18} className="text-primary/80" />
          <span className="text-sm text-foreground/60">
            {t("guidelines.tapInstruction")}
          </span>
        </motion.div>
      )}
    </Page>
  );
}
